import { isTauri } from "@/lib/is-tauri";
import { isWindowsPlatform } from "@/lib/os-platform";
import {
  voiceEnginePreference,
  windowsVoicePreference,
} from "@/desktop/voice-engine-preference";
import { invoke } from "@tauri-apps/api/core";
import { listen } from "@tauri-apps/api/event";
import type { DenkerAgentVoiceId } from "./denker-agent-voices";
export {
  DENKER_AGENT_VOICES,
  type DenkerAgentVoiceId,
} from "./denker-agent-voices";

// Re-exported so callers and test mocks keep one import site for the whole
// agent-voice API, even though the Windows-only parts live in their own files.
export { voicePreviewLine } from "./voice-preview-line";
export {
  cancelAllVoiceEngines,
  previewWindowsSystemVoice,
} from "./windows-voice-preview";

import { voicePreviewLine } from "./voice-preview-line";
import {
  claimPreviewRequest,
  isCurrentPreviewRequest,
} from "./voice-preview-session";
import { cancelAllVoiceEngines } from "./windows-voice-preview";

export type DenkerAgentVoiceCancelReason =
  | "explicit"
  | "stop_key"
  | "voice_barge_in"
  | "user_speech"
  | "new_prompt"
  | "step_changed"
  | "bubble_changed"
  | "bubble_cleared"
  | "same_bubble_muted";

let warmPromise: Promise<void> | null = null;
/** Which engine `warmDenkerAgentVoiceAudio` last got ready, or null. */
let warmedEngine: VoiceEngine | null = null;

// Which engine speaks the agent's replies: "kokoro" (the Denker voice worker,
// Pocket TTS and Kokoro, on every Mac and by default on Windows) or "system"
// (Windows OneCore, which a Windows user can pick instead).
type VoiceEngine = "kokoro" | "system";

/**
 * Whether this machine lets the user pick the engine.
 *
 * Windows does: the Denker voices and the Windows OneCore engine both run well
 * there, so it comes down to taste — the branded Denker voices, or a Windows
 * voice that answers instantly but sounds synthetic. A Mac always speaks with
 * the Denker voices.
 */
export function isVoiceEngineSelectable(): boolean {
  return isWindowsPlatform();
}

/** The engine in effect: the Windows user's choice, else the Denker voices. */
export function currentVoiceEngine(): VoiceEngine {
  if (isVoiceEngineSelectable()) {
    const chosen = voiceEnginePreference();
    if (chosen) return chosen;
  }
  return "kokoro";
}

/** An installed Windows voice, as `denker-system-voices` lists it. */
type SystemVoice = {
  id: string;
  name: string;
  lang: string;
  quality: string;
};

// Bumped on every new list so React components can re-render with it.
let systemVoiceRevision = 0;
const systemVoiceListeners = new Set<() => void>();
let systemVoicesListening = false;
let systemVoicesResolved: (() => void) | null = null;
const systemVoicesFirstList = new Promise<void>((resolve) => {
  systemVoicesResolved = resolve;
});

// ── Windows voice list ────────────────────────────────────────────────────
// The user picks a real Windows voice by name, and the set is whatever they
// have installed — three on a stock machine, more once they add languages from
// Settings. So we keep the list verbatim and let the picker render it directly.

let windowsVoices: SystemVoice[] = [];

function setWindowsVoices(voices: SystemVoice[]): void {
  // Sort natural voices first so they lead the list if Microsoft ever opens
  // them to third-party apps, then alphabetically for a stable order.
  windowsVoices = [...voices].sort((a, b) => {
    const naturalA = a.quality === "natural" ? 0 : 1;
    const naturalB = b.quality === "natural" ? 0 : 1;
    return naturalA - naturalB || a.name.localeCompare(b.name);
  });
  systemVoiceRevision += 1;
  systemVoiceListeners.forEach((fn) => fn());
}

/** The installed Windows voices, in display order. Empty off Windows, and
 *  until the first list arrives. Pair with `subscribeSystemVoices` /
 *  `systemVoiceMappingRevision` for React's `useSyncExternalStore`. */
export function windowsSystemVoices(): SystemVoice[] {
  return windowsVoices;
}

/** Subscribe to new Windows voice lists. Returns an unsubscribe. Pair with
 *  `systemVoiceMappingRevision()` for React useSyncExternalStore. */
export function subscribeSystemVoices(listener: () => void): () => void {
  systemVoiceListeners.add(listener);
  return () => systemVoiceListeners.delete(listener);
}

/** Monotonic revision of the Windows voice list; changes on every new list. */
export function systemVoiceMappingRevision(): number {
  return systemVoiceRevision;
}

/** Start the persistent Windows voice-list listener and request the current
 *  list. Resolves once the FIRST list arrives - at once off Windows, where
 *  there is no list. */
export function ensureSystemVoicesResolved(): Promise<void> {
  if (!systemVoicesListening) {
    systemVoicesListening = true;
    if (isWindowsPlatform()) void resolveWindowsVoices();
    else systemVoicesResolved?.();
  }
  return systemVoicesFirstList;
}

function requestSystemVoiceList(): Promise<void> {
  return invoke<void>("denker_system_voice_list").catch(() => undefined);
}

/** Windows: subscribe to voice lists, then ask for one. The Rust command
 *  answers on a worker thread and always emits, even on failure, so a single
 *  request is enough. */
async function resolveWindowsVoices(): Promise<void> {
  if (!isTauri() || typeof window === "undefined") {
    systemVoicesResolved?.();
    return;
  }
  try {
    // Listen before requesting so the reply cannot arrive first. The listener
    // is permanent: `refreshWindowsSystemVoices` re-requests after the user
    // adds a voice in Windows Settings, and that reply lands here too.
    await listen<{ voices: SystemVoice[] }>("denker-system-voices", (event) => {
      setWindowsVoices(event.payload?.voices ?? []);
      systemVoicesResolved?.();
    });
    await invoke<void>("denker_system_voice_list");
  } catch {
    // Enumeration is best-effort — the Denker voices work without it — but every
    // failure has to unblock the waiters, including a listener that could not
    // be registered. Anything else leaves `ensureSystemVoicesResolved()`
    // pending forever and the picker stuck on its loading state.
    systemVoicesResolved?.();
  }
}

/** Re-read the installed Windows voices.
 *
 *  Voices are added outside the app, in Windows Settings, so the picker calls
 *  this when the window regains focus: a user who just installed one comes back
 *  to find it already listed. */
export function refreshWindowsSystemVoices(): void {
  if (!isWindowsPlatform() || !isTauri()) return;
  void requestSystemVoiceList();
}

// Windows: load the installed voices at once, so Settings can name the chosen
// one before its Voice page has been opened.
if (isTauri() && typeof window !== "undefined" && isWindowsPlatform()) {
  void ensureSystemVoicesResolved();
}

export async function warmDenkerAgentVoiceAudio() {
  if (!isTauri() || typeof window === "undefined") return;
  // Windows OneCore has no model to warm: it lives in the OS and answers on
  // first call.
  if (currentVoiceEngine() === "system") {
    warmedEngine = "system";
    return;
  }
  if (warmedEngine === "kokoro") return;
  if (!warmPromise) {
    warmPromise = invoke<void>("denker_agent_voice_warm_up").catch(
      (error: unknown) => {
        warmPromise = null;
        throw error;
      },
    );
  }
  await warmPromise;
  // Set AFTER the await, not inside the promise's `.then`. On Windows the
  // engine can flip to the system voice and back, and a settled `warmPromise`
  // never re-runs its callbacks — so a `.then` would leave `warmedEngine`
  // stuck on "system" forever, disabling every Preview button.
  warmedEngine = "kokoro";
}

/** Whether the ACTIVE engine is ready to speak.
 *
 *  Keyed on which engine was warmed rather than a plain boolean, because on
 *  Windows the engine can change under us: a user who picks the Windows voice
 *  and then switches back to Denker must re-warm Kokoro, not inherit a "ready"
 *  left over from an engine that needed no warming at all. */
export function isDenkerAgentVoiceAudioReady() {
  return warmedEngine !== null && warmedEngine === currentVoiceEngine();
}

export function primeDenkerAgentVoiceAudio() {
  if (!isTauri() || typeof window === "undefined") return;
  void warmDenkerAgentVoiceAudio().catch(() => {});
}

export async function speakWithDenkerAgentVoice(
  text: string,
  voice: DenkerAgentVoiceId,
  options?: {
    utteranceId?: string;
    preparedId?: string;
    streaming?: boolean;
    executionId?: string;
    ttsSource?: string;
  },
) {
  if (!isTauri() || typeof window === "undefined") return;
  if (currentVoiceEngine() === "system") {
    // Windows persists the chosen voice id outright, so a reply never waits on
    // voice enumeration. An id for a since-uninstalled voice is not an error:
    // Rust falls back to the Windows default rather than going silent. It
    // emits `denker-agent-voice-state` with this utterance id, as the Denker
    // voice path does.
    await invoke("denker_system_voice_speak", {
      text,
      voiceId: windowsVoicePreference(),
      utteranceId: options?.utteranceId,
    });
    return;
  }
  return await invoke<"finished" | "cancelled">("denker_agent_voice_speak", {
    text,
    voice,
    utteranceId: options?.utteranceId,
    preparedId: options?.preparedId,
    // Long onboarding/presentation bubbles need the same synthesis/playback
    // overlap as replies, even when their caller requests standalone speech.
    streaming: text.length > 180 || (options?.streaming ?? true),
    executionId: options?.executionId,
    ttsSource: options?.ttsSource,
  });
}

export async function previewDenkerAgentVoice(
  voice: DenkerAgentVoiceId,
): Promise<boolean> {
  const requestSeq = claimPreviewRequest();
  primeDenkerAgentVoiceAudio();
  await cancelDenkerAgentVoice("new_prompt");
  if (!isCurrentPreviewRequest(requestSeq)) return false;
  try {
    await speakWithDenkerAgentVoice(voicePreviewLine(), voice);
  } catch (error) {
    if (!isCurrentPreviewRequest(requestSeq)) return false;
    throw error;
  }
  return isCurrentPreviewRequest(requestSeq);
}

export function cancelDenkerAgentVoice(
  reason: DenkerAgentVoiceCancelReason = "explicit",
): Promise<void> {
  // Where the engine can change under us, cancel both. Choosing by
  // `currentVoiceEngine()` would send the stop to whichever engine is selected
  // NOW, which after a switch mid-sentence is the one that is not speaking —
  // leaving the other talking with no way to stop it. Cancelling an idle engine
  // costs nothing, so the reliable answer is to cancel both.
  if (isVoiceEngineSelectable()) {
    return cancelAllVoiceEngines();
  }
  // Elsewhere the Denker voices always speak, so target them directly and keep
  // the cancel reason, which their path logs.
  return invoke<void>("denker_audio_cancel", { reason }).catch(() => undefined);
}
